import { ChangeDetectionStrategy, Component, computed, effect, HostListener, input, output, signal, WritableSignal } from '@angular/core';
import { ReactiveFormsModule, UntypedFormControl } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatMenuModule } from '@angular/material/menu';
import { MatTooltipModule } from '@angular/material/tooltip';
import { TranslatePipe } from '@ngx-translate/core';
import { FormControlType } from '../../../forms/form-control-type.model';
import { FormField } from '../../../forms/form-field.model';
import { FormFieldComponent } from '../../../forms/form-field/form-field.component';

import {
  DEFAULT_FORMULA_EDITOR_CONFIG,
  FORMULA_FUNCTION_LABELS,
  FORMULA_OPERATOR_LABELS,
  FormulaEditorConfig,
  getEffectiveFunctions,
  getEffectiveOperators
} from '../../utils/formula-editor-config';
import { FormulaToken, FormulaTokenType } from '../../models/formula-token.model';

// ---------------------------------------------------------------------------
// Token model
// ---------------------------------------------------------------------------

let nextTokenId = 0;

function createToken(type: FormulaTokenType, value: string, label?: string): FormulaToken {
  return { id: `tok-${nextTokenId++}`, type, value, label: label ?? value };
}

// ---------------------------------------------------------------------------
// Tokenizer: formula string → tokens
// ---------------------------------------------------------------------------

export function formulaToTokens(formula: string): FormulaToken[] {
  const trimmed = formula.trim();
  if (!trimmed) {
    return [];
  }

  const tokens: FormulaToken[] = [];
  let i = 0;

  while (i < trimmed.length) {
    const ch = trimmed[i];

    // Whitespace — skip
    if (/\s/.test(ch)) {
      i++;
      continue;
    }

    // Numbers (integers, decimals)
    if (/\d/.test(ch)) {
      let num = '';
      while (i < trimmed.length && /[\d.]/.test(trimmed[i])) {
        num += trimmed[i];
        i++;
      }
      tokens.push(createToken('number', num));
      continue;
    }

    // Opening paren
    if (ch === '(') {
      // Check if previous token is a function-open (already has the '(')
      tokens.push(createToken('paren-open', '(', '('));
      i++;
      continue;
    }

    // Closing paren
    if (ch === ')') {
      tokens.push(createToken('paren-close', ')', ')'));
      i++;
      continue;
    }

    // Comma
    if (ch === ',') {
      tokens.push(createToken('comma', ',', ','));
      i++;
      continue;
    }

    // Operators (single char): + - * / ^ % > < & | ~ = ! ? :
    if (/[+\-*/^%><&|~=!?:]/.test(ch)) {
      // Check multi-char operators (order matters: longest first)
      if (i + 2 < trimmed.length) {
        const three = ch + trimmed[i + 1] + trimmed[i + 2];
        if (['>>>'].includes(three)) {
          const opLabel = FORMULA_OPERATOR_LABELS[three] ?? three;
          tokens.push(createToken('operator', three, opLabel));
          i += 3;
          continue;
        }
      }
      if (i + 1 < trimmed.length) {
        const two = ch + trimmed[i + 1];
        if (['>=', '<=', '==', '!=', '<<', '>>', '&&', '||'].includes(two)) {
          const opLabel = FORMULA_OPERATOR_LABELS[two] ?? two;
          tokens.push(createToken('operator', two, opLabel));
          i += 2;
          continue;
        }
      }
      const label = FORMULA_OPERATOR_LABELS[ch] ?? ch;
      tokens.push(createToken('operator', ch, label));
      i++;
      continue;
    }

    // Identifiers: variables, functions, word-operators
    if (/[a-zA-Z_]/.test(ch)) {
      let name = '';
      while (i < trimmed.length && /[a-zA-Z0-9_-]/.test(trimmed[i])) {
        name += trimmed[i];
        i++;
      }

      // Word operators
      if (['and', 'or', 'xor', 'not', 'to', 'in'].includes(name)) {
        tokens.push(createToken('operator', name, FORMULA_OPERATOR_LABELS[name] ?? name));
        continue;
      }

      // Check if followed by '(' → function
      if (i < trimmed.length && trimmed[i] === '(') {
        tokens.push(createToken('function-open', `${name}(`, FORMULA_FUNCTION_LABELS[name] ?? name));
        i++; // consume the '('
        continue;
      }

      // Variable
      tokens.push(createToken('variable', name, name));
      continue;
    }

    // Unknown — skip
    i++;
  }

  return tokens;
}

// ---------------------------------------------------------------------------
// Serializer: tokens → formula string
// ---------------------------------------------------------------------------

export function tokensToFormula(tokens: FormulaToken[]): string {
  const parts: string[] = [];

  for (let i = 0; i < tokens.length; i++) {
    const tok = tokens[i];
    const prev = i > 0 ? tokens[i - 1] : null;

    switch (tok.type) {
      case 'variable':
      case 'number':
        // Add space before if previous is a value token
        if (prev && (prev.type === 'variable' || prev.type === 'number' || prev.type === 'paren-close')) {
          parts.push(' ');
        }
        parts.push(tok.value);
        break;

      case 'operator':
        parts.push(` ${tok.value} `);
        break;

      case 'function-open':
        if (prev && (prev.type === 'variable' || prev.type === 'number' || prev.type === 'paren-close')) {
          parts.push(' ');
        }
        parts.push(tok.value); // e.g. "sqrt("
        break;

      case 'paren-open':
        if (prev && (prev.type === 'variable' || prev.type === 'number' || prev.type === 'paren-close')) {
          parts.push(' ');
        }
        parts.push('(');
        break;

      case 'paren-close':
        parts.push(')');
        break;

      case 'comma':
        parts.push(', ');
        break;
    }
  }

  return parts.join('').replace(/\s+/g, ' ').trim();
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

@Component({
  selector: 'iot-platform-ui-formula-builder',
  imports: [ReactiveFormsModule, FormFieldComponent, MatButtonModule, MatIconModule, MatMenuModule, MatTooltipModule, TranslatePipe],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <div class="formula-builder" data-test="formula-builder">
      <!-- Toolbar: insert actions -->
      <div class="formula-builder-toolbar mb-3 flex flex-wrap items-center gap-2">
        <!-- Variable selector -->
        <div class="formula-builder-var-field" data-test="formula-builder-variable-field">
          <iot-platform-ui-form-field [control]="variableControl" [field]="$any(variableField())" />
        </div>

        <!-- Number input -->
        <div class="formula-builder-num-field-wrapper flex items-center gap-2" data-test="formula-builder-number-field">
          <iot-platform-ui-form-field [control]="numberControl" [field]="$any(numberField)" />

          <button
            mat-icon-button
            (click)="insertNumber()"
            [matTooltip]="'DEVICES.SYNOPTIC.FORMULA.INSERT_NUMBER_TOOLTIP' | translate"
            data-test="formula-builder-add-number"
          >
            <mat-icon>add</mat-icon>
          </button>
        </div>

        <!-- Operator buttons -->
        <div class="flex flex-wrap items-center gap-1">
          @for (op of allowedOperatorsList(); track op.value) {
            <button
              mat-stroked-button
              class="formula-builder-op-btn"
              [matTooltip]="op.label"
              (click)="insertOperator(op.value)"
              [attr.data-test]="'formula-builder-op-' + op.value"
            >
              {{ op.value }}
            </button>
          }
        </div>

        <!-- Parentheses & comma -->
        <button mat-stroked-button (click)="insertParen('(')" matTooltip="(" data-test="formula-builder-paren-open">(</button>
        <button mat-stroked-button (click)="insertParen(')')" matTooltip=")" data-test="formula-builder-paren-close">)</button>

        <button
          mat-stroked-button
          (click)="insertComma()"
          [matTooltip]="'DEVICES.SYNOPTIC.FORMULA.INSERT_COMMA_TOOLTIP' | translate"
          data-test="formula-builder-comma"
        >
          ,
        </button>

        <!-- Function menu -->
        @if (allowedFunctionsList().length) {
          <button mat-stroked-button [matMenuTriggerFor]="fnMenu" data-test="formula-builder-fn-btn">
            <mat-icon>functions</mat-icon>
            <span>{{ 'DEVICES.SYNOPTIC.FORMULA.INSERT_FUNCTION' | translate }}</span>
          </button>
          <mat-menu #fnMenu="matMenu" class="formula-builder-fn-menu">
            @for (fn of allowedFunctionsList(); track fn.value) {
              <button mat-menu-item (click)="insertFunction(fn.value)">
                <span class="font-mono">{{ fn.value }}</span>
                <span class="ml-2 text-xs" style="color: var(--mat-sys-on-surface-variant, #49454f)">{{ fn.label }}</span>
              </button>
            }
          </mat-menu>
        }
      </div>

      <!-- Replace mode hint -->
      @if (selectedIndex() !== null) {
        <div class="formula-replace-hint" data-test="formula-replace-hint">
          <mat-icon class="formula-replace-hint-icon">swap_horiz</mat-icon>
          <span>{{ 'DEVICES.SYNOPTIC.FORMULA.REPLACE_MODE_HINT' | translate }}</span>
          <button
            mat-icon-button
            class="formula-replace-hint-close"
            (click)="deselectChip()"
            [matTooltip]="'DEVICES.SYNOPTIC.FORMULA.DESELECT_CHIP' | translate"
            data-test="formula-replace-hint-close"
          >
            <mat-icon>close</mat-icon>
          </button>
        </div>
      }

      <!-- Token display area -->
      <div class="formula-builder-tokens" data-test="formula-builder-tokens" (click)="deselectChip()">
        @if (!tokens().length) {
          <span class="formula-builder-placeholder">
            {{ 'DEVICES.SYNOPTIC.FORMULA.BUILDER_PLACEHOLDER' | translate }}
          </span>
        }
        @for (token of tokens(); track token.id; let i = $index) {
          <span
            class="formula-token"
            [class]="'formula-token--' + token.type"
            [class.formula-token--selected]="selectedIndex() === i"
            [attr.data-test]="'formula-token-' + i"
            [matTooltip]="
              selectedIndex() === i
                ? ('DEVICES.SYNOPTIC.FORMULA.CHIP_CLICK_TO_REPLACE' | translate)
                : ('DEVICES.SYNOPTIC.FORMULA.CHIP_CLICK_TO_SELECT' | translate)
            "
            (click)="onChipClick(i, $event)"
          >
            <span class="formula-token-value">{{ token.type === 'function-open' ? token.value.slice(0, -1) : token.value }}</span>
            @if (token.type === 'function-open') {
              <span class="formula-token-paren">(</span>
            }
            <button class="formula-token-remove" (click)="onRemoveTokenClick(i, $event)" [attr.data-test]="'formula-token-remove-' + i">
              <mat-icon class="formula-token-remove-icon">close</mat-icon>
            </button>
          </span>
        }
      </div>

      <!-- Clear all -->
      @if (tokens().length) {
        <div class="mt-1 flex justify-end">
          <button mat-button color="warn" (click)="clearAll()" data-test="formula-builder-clear">
            <mat-icon>delete_sweep</mat-icon>
            {{ 'DEVICES.SYNOPTIC.FORMULA.CLEAR_ALL' | translate }}
          </button>
        </div>
      }
    </div>
  `,
  styles: `
    .formula-builder-var-field {
      min-width: 160px;
      max-width: 220px;
      font-size: 13px;

      mat-form-field {
        width: 100%;
      }
    }

    .formula-builder-num-field-wrapper {
      min-width: 80px;
      max-width: 120px;
      font-size: 13px;

      mat-form-field {
        width: 100%;
      }
    }

    .formula-builder-op-btn {
      min-width: 36px !important;
      padding: 0 8px !important;
      font-family: 'JetBrains Mono', 'Fira Code', monospace;
      font-weight: 700;
      font-size: 16px;
    }

    .formula-builder-tokens {
      display: flex;
      flex-wrap: wrap;
      align-items: center;
      gap: 4px;
      min-height: 48px;
      padding: 8px 12px;
      border: 1px solid var(--mat-sys-outline-variant, #c4c7c5);
      border-radius: 8px;
      background: var(--mat-sys-surface-container-low, #f7f2fa);
    }

    .formula-builder-placeholder {
      color: var(--mat-sys-on-surface-variant, #49454f);
      font-size: 13px;
      opacity: 0.6;
    }

    .formula-token {
      display: inline-flex;
      align-items: center;
      gap: 2px;
      padding: 2px 6px 2px 8px;
      border-radius: 16px;
      font-family: 'JetBrains Mono', 'Fira Code', monospace;
      font-size: 13px;
      font-weight: 600;
      line-height: 1.6;
      white-space: nowrap;
      cursor: pointer;
      transition: box-shadow 0.15s ease;
    }

    .formula-token:hover:not(.formula-token--selected) {
      box-shadow: 0 0 0 2px color-mix(in srgb, var(--mat-sys-primary, #6750a4) 30%, transparent);
    }

    .formula-token--variable {
      background: #e3f2fd;
      color: #1565c0;
    }

    .formula-token--operator {
      background: #fff3e0;
      color: #e65100;
      font-weight: 700;
    }

    .formula-token--function-open {
      background: #f3e5f5;
      color: #7b1fa2;
    }

    .formula-token--number {
      background: #e8f5e9;
      color: #2e7d32;
    }

    .formula-token--paren-open,
    .formula-token--paren-close {
      background: var(--mat-sys-surface-container, #ece6f0);
      color: var(--mat-sys-on-surface, #1d1b20);
      font-weight: 700;
      font-size: 15px;
    }

    .formula-token--comma {
      background: transparent;
      color: var(--mat-sys-on-surface-variant, #49454f);
      padding: 2px 2px;
    }

    .formula-token-paren {
      opacity: 0.6;
    }

    .formula-token-remove {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      width: 16px;
      height: 16px;
      border: none;
      background: transparent;
      cursor: pointer;
      padding: 0;
      margin-left: 2px;
      border-radius: 50%;
      opacity: 0.5;
    }

    .formula-token-remove:hover {
      opacity: 1;
      background: rgba(0, 0, 0, 0.08);
    }

    .formula-token-remove-icon {
      font-size: 14px !important;
      width: 14px !important;
      height: 14px !important;
    }

    .formula-token--selected {
      outline: 2px solid var(--mat-sys-primary, #6750a4);
      outline-offset: 2px;
      box-shadow: 0 0 0 4px color-mix(in srgb, var(--mat-sys-primary, #6750a4) 18%, transparent);
      cursor: pointer;
    }

    .formula-replace-hint {
      display: flex;
      align-items: center;
      gap: 6px;
      padding: 4px 8px 4px 12px;
      margin-bottom: 6px;
      background: color-mix(in srgb, var(--mat-sys-primary, #6750a4) 8%, transparent);
      border: 1px solid color-mix(in srgb, var(--mat-sys-primary, #6750a4) 35%, transparent);
      border-radius: 8px;
      font-size: 12px;
      color: var(--mat-sys-primary, #6750a4);
    }

    .formula-replace-hint-icon {
      font-size: 16px !important;
      width: 16px !important;
      height: 16px !important;
      flex-shrink: 0;
    }

    .formula-replace-hint-close {
      margin-left: auto;
      width: 28px !important;
      height: 28px !important;
    }
  `
})
export class FormulaBuilderComponent {
  /** Available variable names */
  readonly variables = input.required<string[]>();

  /** Editor configuration for restrictions */
  readonly config = input<FormulaEditorConfig>(DEFAULT_FORMULA_EDITOR_CONFIG);

  /** Initial formula string to parse into tokens */
  readonly formula = input<string>('');

  /** Emits the formula string on every token change */
  readonly formulaChange = output<string>();

  /** Current token list */
  readonly tokens: WritableSignal<FormulaToken[]> = signal([]);

  /** Index of the currently selected chip (null = no selection / append mode) */
  readonly selectedIndex = signal<number | null>(null);

  /** Reactive form control for the variable autocomplete */
  readonly variableControl = new UntypedFormControl('');

  /** Reactive form control for the number input */
  readonly numberControl = new UntypedFormControl(null);

  /** Field definition for the variable autocomplete (reacts to variables() changes) */
  readonly variableField = computed<Partial<FormField>>(() => ({
    type: FormControlType.AUTO_COMPLETE,
    name: signal('variable'),
    label: signal('DEVICES.SYNOPTIC.FORMULA.INSERT_VARIABLE'),
    placeholder: signal(''),
    items: this.variables,
    displayBy: (item: string) => item,
    clearOnSelect: signal(true),
    selectionChange: (event: { option: { value: string } }) => {
      const value = event?.option?.value;
      if (value) this.insertVariable(value);
    }
  }));

  /** Field definition for the number input */
  readonly numberField: Partial<FormField> = {
    type: FormControlType.NUMBER,
    name: signal('number'),
    label: signal('DEVICES.SYNOPTIC.FORMULA.INSERT_NUMBER'),
    placeholder: signal(''),
    onKeyupEnter: () => this.insertNumber()
  };

  /** Allowed operators as list for the toolbar */
  readonly allowedOperatorsList = computed(() => {
    const cfg = this.config();
    const ops = getEffectiveOperators(cfg);

    // Expand the composite '?:' operator into its two component buttons
    const expanded: string[] = [];
    for (const op of ops) {
      if (op === '?:') {
        expanded.push('?', ':');
      } else {
        expanded.push(op);
      }
    }

    // Surface the conditional-only operators in the toolbar — handy for building conditions
    for (const op of cfg.inConditionalOnlyOperators ?? []) {
      if (!expanded.includes(op)) {
        expanded.push(op);
      }
    }

    const result = expanded
      .filter((op) => !['and', 'or', 'xor', 'not', 'to', 'in', '='].includes(op))
      .map((op) => ({ value: op, label: FORMULA_OPERATOR_LABELS[op] ?? op }));
    return result;
  });

  /** Allowed functions as list for the menu */
  readonly allowedFunctionsList = computed(() => {
    const fns = getEffectiveFunctions(this.config());
    return [...fns].map((fn) => ({ value: fn, label: FORMULA_FUNCTION_LABELS[fn] ?? fn }));
  });

  constructor() {
    // Reactively sync the formula input to the token list.
    // Handles late input binding when the component is created inside @if
    // (e.g., switching from text to visual mode in the dialog).
    effect(() => {
      const incoming = this.formula();
      const tokenizedFormula = formulaToTokens(incoming ?? '');
      const formattedIncoming = tokensToFormula(tokenizedFormula);
      const currentFormula = tokensToFormula(this.tokens());
      if (formattedIncoming !== currentFormula) {
        this.tokens.set(incoming ? tokenizedFormula : []);
      }
    });
  }

  // ---------------------------------------------------------------------------
  // Chip selection
  // ---------------------------------------------------------------------------

  selectChip(index: number): void {
    this.selectedIndex.set(index);
  }

  deselectChip(): void {
    this.selectedIndex.set(null);
  }

  onChipClick(index: number, event: MouseEvent): void {
    event.stopPropagation();
    // Toggle off if already selected; select otherwise
    this.selectedIndex.set(this.selectedIndex() === index ? null : index);
  }

  onRemoveTokenClick(index: number, event: MouseEvent): void {
    event.stopPropagation();
    this.removeToken(index);
  }

  // ---------------------------------------------------------------------------
  // Keyboard shortcuts (host element — bubbles from focused children)
  // ---------------------------------------------------------------------------

  @HostListener('keydown', ['$event'])
  onKeyDown(event: KeyboardEvent): void {
    if (event.key === 'Escape') {
      this.deselectChip();
      return;
    }

    // Delete / Backspace only removes selected chip when focus is NOT inside a text input
    const target = event.target as HTMLElement;
    const isInputFocused = target.tagName === 'INPUT' || target.tagName === 'TEXTAREA';

    if (!isInputFocused && (event.key === 'Delete' || event.key === 'Backspace')) {
      const idx = this.selectedIndex();
      if (idx !== null) {
        event.preventDefault();
        this.removeToken(idx);
      }
    }
  }

  // ---------------------------------------------------------------------------
  // Insert actions
  // ---------------------------------------------------------------------------

  insertVariable(name: string): void {
    this.addToken(createToken('variable', name));
  }

  insertOperator(op: string): void {
    const label = FORMULA_OPERATOR_LABELS[op] ?? op;
    this.addToken(createToken('operator', op, label));
  }

  insertFunction(fn: string): void {
    const label = FORMULA_FUNCTION_LABELS[fn] ?? fn;
    this.addToken(createToken('function-open', `${fn}(`, label));
  }

  insertParen(paren: '(' | ')'): void {
    const type = paren === '(' ? 'paren-open' : 'paren-close';
    this.addToken(createToken(type, paren));
  }

  insertComma(): void {
    this.addToken(createToken('comma', ',', ','));
  }

  insertNumber(): void {
    const val = this.numberControl.value?.toString().trim();
    if (val && !isNaN(Number(val))) {
      this.addToken(createToken('number', val));
      this.numberControl.reset();
    }
  }

  removeToken(index: number): void {
    this.tokens.update((t) => t.filter((_, i) => i !== index));
    this.deselectChip();
    this.emitFormula();
  }

  clearAll(): void {
    this.tokens.set([]);
    this.deselectChip();
    this.emitFormula();
  }

  // ---------------------------------------------------------------------------
  // Private helpers
  // ---------------------------------------------------------------------------

  /**
   * Core insertion logic.
   * - If a chip is selected → replace it in-place and clear selection.
   * - Otherwise → append to the end of the token list.
   */
  private addToken(token: FormulaToken): void {
    const idx = this.selectedIndex();
    if (idx !== null) {
      this.tokens.update((t) => {
        const copy = [...t];
        copy[idx] = token;
        return copy;
      });
      this.deselectChip();
    } else {
      this.tokens.update((t) => [...t, token]);
    }
    this.emitFormula();
  }

  private emitFormula(): void {
    const formula = tokensToFormula(this.tokens());
    this.formulaChange.emit(formula);
  }
}
